import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import axiosInstance from "../../interceptor/axiosInstance";
import { Report } from "../../models/Report/report.model";
import { deserialize, serialize } from "serializr";
import Notification from "../../shared/components/Notification";
import { NotificationTypes } from "../../enums/notificationTypes";
import { DownloadFileTypeEnum } from "../../enums/downloadFileType.enum";
import { CaseDetail } from "../../models/CaseDetail/caseDetail.model";
import { ReportTemplate } from "../../models/ReportTemplate/reportTemplate.model";
import { formatFileNameFromCaseDetail } from "../../shared/utils/dataFormatConverter";

export default class ReportService {
  static fetchReports(
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    axiosInstance
      .get(ApiRoutes.REPORTS)
      .then((response) => {
        const reports = deserialize(Report, response.data["reports"]);
        onSuccess(reports);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showReport(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + reportId;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        if (response.data.report) {
          const report = deserialize(Report, response.data["report"]);
          onSuccess(report);
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showAdminReport(
    caseDetailId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_REPORTS.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL)
      .then((response) => {
        if (response.data.report) {
          const report = deserialize(Report, response.data["report"]);
          onSuccess(report);
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static uploadDirectReport(
    reportId: number,
    filePath: string,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void,
  ) {
    const API_URL = ApiRoutes.UPLOAD_DIRECT_REPORT.replace(":reportId", reportId.toString());
    axiosInstance
      .put(API_URL, {
        filename: filePath
      })
      .then((response) => {
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static getSignedURLForDirectReport(
    reportId: number,
    extension: string,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void,
  ) {
    const API_URL = ApiRoutes.GET_SIGNED_URL_FOR_DIRECT_REPORTS.replace(
        ":reportId",
        reportId.toString()
    ) + "?ext=" + extension;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        onSuccess({
          url: response?.data?.url,
          filePath: response?.data?.file_name
        });
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static createReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void,
    disableNotification: boolean = false
  ) {
    const reportJSON = { report: serialize(report) };
    axiosInstance
      .post(ApiRoutes.REPORTS, reportJSON)
      .then((response) => {
        if(!disableNotification) {
          Notification({
            message: "Report created",
            type: NotificationTypes.SUCCESS,
          });
        }
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void,
    silent: boolean = false
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + report.id;
    const reportJSON = { report: serialize(report) };
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        if (!silent) {
          Notification({
            message: "Report updated",
            type: NotificationTypes.SUCCESS,
          });
        }
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        if (error?.response?.status === 423) {
          onError(error.response.data);
        } else {
          onError(null);
        }
      })
      .finally(() => {
        onFinal();
      });
  }

  static acquireReportLock(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORT_LOCK.replace(":reportId", reportId.toString());
    axiosInstance
      .post(API_URL)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        onFinal();
      });
  }

  static releaseReportLock(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORT_LOCK.replace(":reportId", reportId.toString());
    axiosInstance
      .delete(API_URL)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static checkReportLock(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORT_LOCK.replace(":reportId", reportId.toString());
    axiosInstance
      .get(API_URL)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        onFinal();
      });
  }

  static checkReportLockAsync(reportId: number): Promise<{
    locked: boolean;
    locked_by: { id: number; type: string; name: string; display_name: string } | null;
  }> {
    const API_URL = ApiRoutes.REPORT_LOCK.replace(":reportId", reportId.toString());
    return axiosInstance
      .get(API_URL)
      .then((response) => response.data)
      .catch(() => ({ locked: false, locked_by: null }));
  }

  static forceReleaseReportLock(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORT_LOCK_FORCE.replace(":reportId", reportId.toString());
    axiosInstance
      .delete(API_URL)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static publishReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    let API_URL = ApiRoutes.PUBLISH_REPORT;
    if (report?.id) {
      API_URL = API_URL.replace(":reportId", report.id.toString());
    }
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report published",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static approveAndPublishReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    let API_URL = ApiRoutes.APPROVE_AND_PUBLISH_REPORT;
    if (report?.id) {
      API_URL = API_URL.replace(":reportId", report.id.toString());
    }
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report approved and published",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static sendForApproval(
    report: Report,
    caseDetailId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    const API_URL = ApiRoutes.SEND_FOR_APPROVAL.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .post(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report sent for approval",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static downloadDirectReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetailId) {
      let API_URL = ApiRoutes.DOWNLOAD_DIRECT_REPORT.replace(
        ":caseDetailId",
        caseDetailId.toString()
      ) + '?report_id=' + reportId;
      let api = axiosInstance.get(API_URL);
      api
        .then((response) => {
          onSuccess(response.data.url);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }

  static downloadScanCenterDirectReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetailId) {
      let API_URL = ApiRoutes.SCAN_CENTER_DOWNLOAD_DIRECT_REPORT.replace(
        ":caseDetailId",
        caseDetailId.toString()
      ) + '?report_id=' + reportId;
      let api = axiosInstance.get(API_URL);
      api
        .then((response) => {
          onSuccess(response.data.url);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }


  static downloadReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: CaseDetail,
    withLetterHead: boolean,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetail?.id) {
      let API_URL = ApiRoutes.DOWNLOAD_PDF.replace(
        ":caseDetailId",
        caseDetail?.id.toString()
      ) + '?report_id=' + reportId;
      let api = axiosInstance.get(API_URL);
      if (fileType === DownloadFileTypeEnum.WORD) {
        API_URL = ApiRoutes.DOWNLOAD_WORD.replace(
          ":caseDetailId",
          caseDetail?.id.toString()
        ) + '?report_id=' + reportId;
        api = axiosInstance.get(API_URL, { responseType: "blob" });
      }
      api
        .then((response) => {
          if (fileType === DownloadFileTypeEnum.WORD) {
            const blob = new Blob([response.data], {
              type:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadURL;
            link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
            link.click();
          } else {
            onSuccess(
              withLetterHead
                ? response.data.letter_head_report
                : response.data.report
            );
          }
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }

  static downloadApprovalReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: CaseDetail,
    withLetterHead: boolean,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetail?.id) {
      let API_URL = ApiRoutes.APPROVAL_DOWNLOAD_PDF.replace(
        ":caseDetailId",
        caseDetail?.id.toString()
      );
      let api = axiosInstance.get(API_URL);
      if (fileType === DownloadFileTypeEnum.WORD) {
        API_URL = ApiRoutes.APPROVAL_DOWNLOAD_WORD.replace(
          ":caseDetailId",
          caseDetail?.id.toString()
        );
        api = axiosInstance.get(API_URL, { responseType: "blob" });
      }
      api
        .then((response) => {
          if (fileType === DownloadFileTypeEnum.WORD) {
            const blob = new Blob([response.data], {
              type:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadURL;
            link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
            link.click();
          } else {
            onSuccess(
              withLetterHead
                ? response.data.letter_head_report
                : response.data.report
            );
          }
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }

  static downloadScanCenterReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: CaseDetail,
    withLetterHead: boolean,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const caseDetailId = caseDetail?.id ?? 0;
    let API_URL = ApiRoutes.SCAN_CENTER_DOWNLOAD_PDF.replace(
      ":caseDetailId",
      caseDetailId.toString()
    ) + '?report_id=' + reportId;
    let api = axiosInstance.get(API_URL);
    if (fileType === DownloadFileTypeEnum.WORD) {
      API_URL = ApiRoutes.SCAN_CENTER_DOWNLOAD_WORD.replace(
        ":caseDetailId",
        caseDetailId.toString()
      ) + '?report_id=' + reportId;
      api = axiosInstance.get(API_URL, { responseType: "blob" });
    }
    api
      .then((response) => {
        if (fileType === DownloadFileTypeEnum.WORD) {
          const blob = new Blob([response.data], {
            type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadURL;
          link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
          link.click();
        } else {
          onSuccess(
            withLetterHead
              ? response.data.letter_head_report
              : response.data.report
          );
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static previewScanCenterReport(
      caseDetailId: number,
      reportId: number,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.SCAN_CENTER_PREVIEW_REPORT.replace(
        ":caseDetailId",
        caseDetailId.toString()
    );
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
          responseType: "blob",
        })
        .then((response) => {
          onSuccess(response.data);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static previewReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.PREVIEW_REPORT.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL, {
        params: reportJSON,
        responseType: "blob",
      })
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static previewApprovalReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.APPROVAL_PREVIEW_REPORT.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL, {
        params: reportJSON,
        responseType: "blob",
      })
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static deleteReport(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + reportId;
    axiosInstance
      .delete(API_URL)
      .then((response) => {
        onSuccess();
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static getVoiceToTextInfo(
      user_type: string | undefined,
      user_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type };
    let API_URL = ApiRoutes.VOICE_TO_TEXT_INFO;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
        })
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static storeVoiceToTextSessionData(
      user_type: string | undefined,
      user_id: number | undefined,
      used_seconds: number | undefined,
      case_detail_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type, user_id: user_id, used_seconds: used_seconds, case_detail_id: case_detail_id };
    let API_URL = ApiRoutes.VOICE_TO_TEXT_SESSIONS;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getAiReportingInfo(
      user_type: string | undefined,
      user_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type };
    let API_URL = ApiRoutes.AI_REPORTING_INFO;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
        })
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static storeAiReportingSessionData(
      user_type: string | undefined,
      user_id: number | undefined,
      used_credits: number | undefined,
      case_detail_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type, user_id: user_id, used_credits: 1, case_detail_id: case_detail_id };
    let API_URL = ApiRoutes.AI_REPORTING_SESSIONS;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static generateGeminiReport(
      report: Report,
      textContent: string | undefined,
      templateContent: string | undefined,
      reportTemplates: ReportTemplate[] = [],
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = {
      report_content: textContent,
      text_content: templateContent || "",
      report_templates: reportTemplates.map((template) => ({
        id: template.id,
        name: template.name || "",
        content: template.content || "",
      })),
    };

    let API_URL = process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GENERATE_AI_REPORT;

    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          Notification({
            message: "Radiolinq AI Assist Done!",
            type: NotificationTypes.SUCCESS,
          });
          const report = response.data["generated_text"];
          onSuccess(report);
        })
        .catch((error) => {
          console.error("Error generating report:", error);
          const errorMessage =
            error?.response?.data?.detail ||
            "Smart Report generation failed";
          onError(errorMessage);
        })
        .finally(() => {
          onFinal();
        });
  }

  static generateUsgReport(
      case_detail_id: number | undefined,
      report: Report,
      textContent: string | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = {
      report_content: textContent,
    };

    let API_URL = process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GENERATE_USG_REPORT;

    if (case_detail_id) {
      API_URL = API_URL.replace(":caseDetailId", case_detail_id.toString());
    }

    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          Notification({
            message: "USG report generated successfully!",
            type: NotificationTypes.SUCCESS,
          });
          const report = response.data["generated_text"];
          onSuccess(report);
        })
        .catch((error) => {
          console.error("Error generating report:", error);
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getAiDiagnosisReport(
      caseDetailId: number,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.AI_DIAGNOSIS_DATA + caseDetailId.toString())
        .then((response) => {
          const ai_report_template = response.data["ai_report_template"];
          const status = response.data["status"];
          onSuccess(ai_report_template, status);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getOrganList(
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GET_ORGANS_LIST)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }
  static getOrganPathologyDetails(
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  )
  {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GET_ORGANS_PATHOLOGY_DETAILS)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

}
